import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  DatePicker,
  Button,
  Select,
  message,
  Spin,
  Statistic
} from "antd";
import { ArrowLeftOutlined, CalendarOutlined, InboxOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import moment from "moment";
import request from "helper/request";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import "./PoolReport.scss";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const { RangePicker } = DatePicker;
const { Option } = Select;

const breadCrumbs = [
  { text: "REPORTS", path: "/admin/reports" },
  { text: "POOL REPORT" }
];

const PoolReport = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  // Get last month and this month as default
  const defaultStartDate = moment().subtract(1, "month").startOf("month");
  const defaultEndDate = moment().endOf("month");

  const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
  const [summaryPeriod, setSummaryPeriod] = useState("daily");
  const [reportData, setReportData] = useState(null);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleSummaryPeriodChange = (value) => {
    setSummaryPeriod(value);
  };

  const handleSubmit = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error("Please select a date range");
      return;
    }

    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        summaryPeriod: summaryPeriod
      };

      const response = await request.get("/admin/reports/pool", { params });

      if (response) {
        setReportData(response);
        message.success("Pool report generated successfully");
      }
    } catch (error) {
      console.error("Error generating pool report:", error);
      message.error("Failed to generate pool report");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push("/admin/reports");
  };

  // Generate chart colors
  const generateColors = (count) => {
    const colors = [
      "rgba(54, 162, 235, 0.8)", // blue
      "rgba(255, 99, 132, 0.8)", // red
      "rgba(255, 206, 86, 0.8)", // yellow
      "rgba(75, 192, 192, 0.8)", // teal
      "rgba(153, 102, 255, 0.8)", // purple
      "rgba(255, 159, 64, 0.8)", // orange
      "rgba(199, 199, 199, 0.8)", // gray
      "rgba(83, 102, 255, 0.8)", // indigo
      "rgba(255, 99, 255, 0.8)", // pink
      "rgba(99, 255, 132, 0.8)" // green
    ];

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  };

  // Prepare chart data for package types
  const getPackageTypeChartData = () => {
    if (!reportData) return null;

    const datasets = reportData.byPackageType.map((typeData, index) => ({
      label: typeData.packageTypeName,
      data: typeData.data.map((d) => d.count),
      backgroundColor: generateColors(reportData.byPackageType.length)[index],
      borderColor: generateColors(reportData.byPackageType.length)[index].replace(
        "0.8",
        "1"
      ),
      borderWidth: 1
    }));

    return {
      labels: reportData.periods,
      datasets
    };
  };

  // Prepare chart data for clients
  const getClientChartData = () => {
    if (!reportData) return null;

    const datasets = reportData.byClient.map((clientData, index) => ({
      label: clientData.clientName,
      data: clientData.data.map((d) => d.count),
      backgroundColor: generateColors(reportData.byClient.length)[index],
      borderColor: generateColors(reportData.byClient.length)[index].replace("0.8", "1"),
      borderWidth: 1
    }));

    return {
      labels: reportData.periods,
      datasets
    };
  };

  // Prepare chart data for pool price by package type
  const getPackageTypePriceChartData = () => {
    if (!reportData) return null;

    const datasets = reportData.byPackageType.map((typeData, index) => ({
      label: typeData.packageTypeName,
      data: typeData.data.map((d) => d.poolPrice),
      backgroundColor: generateColors(reportData.byPackageType.length)[index],
      borderColor: generateColors(reportData.byPackageType.length)[index].replace(
        "0.8",
        "1"
      ),
      borderWidth: 1
    }));

    return {
      labels: reportData.periods,
      datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top"
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  const priceChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: {
          callback: function (value) {
            return "$" + value.toFixed(2);
          }
        }
      }
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += "$" + context.parsed.y.toFixed(2);
            return label;
          }
        }
      }
    }
  };

  return (
    <div className="pool-report">
      <AdminBreadCrumb breadCrumbs={breadCrumbs} />

      <div className="pool-report-content">
        <div className="report-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginBottom: "16px" }}
          >
            Back to Reports
          </Button>
          <h1 style={{ color: "#1890ff", marginBottom: "8px" }}>
            <InboxOutlined style={{ marginRight: "8px" }} />
            Pool Packages Report
          </h1>
          <p style={{ color: "#666", marginBottom: "24px" }}>
            View pool packages dropped off, broken down by package type and client
          </p>
        </div>

        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <CalendarOutlined style={{ marginRight: "8px" }} />
                <strong>Date Range</strong>
              </div>
              <RangePicker
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                disabledDate={(current) => current && current > moment().endOf("day")}
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <strong>Summary Period</strong>
              </div>
              <Select
                value={summaryPeriod}
                onChange={handleSummaryPeriodChange}
                style={{ width: "100%" }}
              >
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <div style={{ marginBottom: "8px", visibility: "hidden" }}>
                <strong>Spacer</strong>
              </div>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                style={{ width: "100%" }}
              >
                Generate Report
              </Button>
            </Col>
          </Row>
        </Card>

        {loading && (
          <div style={{ textAlign: "center", marginTop: "48px" }}>
            <Spin size="large" tip="Generating report..." />
          </div>
        )}

        {!loading && reportData && (
          <>
            <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Total Packages"
                    value={reportData.totalPackages}
                    prefix={<InboxOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Total Pool Price"
                    value={reportData.totalPoolPrice}
                    precision={2}
                    prefix="$"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Package Types"
                    value={reportData.byPackageType.length}
                  />
                </Card>
              </Col>
            </Row>

            <Card
              title="Dropped Off Pool Packages by Package Type"
              style={{ marginTop: "24px" }}
            >
              <div style={{ height: "400px" }}>
                {getPackageTypeChartData() && (
                  <Bar data={getPackageTypeChartData()} options={chartOptions} />
                )}
              </div>
            </Card>

            <Card title="Pool Price by Package Type" style={{ marginTop: "24px" }}>
              <div style={{ height: "400px" }}>
                {getPackageTypePriceChartData() && (
                  <Bar
                    data={getPackageTypePriceChartData()}
                    options={priceChartOptions}
                  />
                )}
              </div>
            </Card>

            <Card
              title="Dropped Off Pool Packages by Client"
              style={{ marginTop: "24px" }}
            >
              <div style={{ height: "400px" }}>
                {getClientChartData() && (
                  <Bar data={getClientChartData()} options={chartOptions} />
                )}
              </div>
            </Card>
          </>
        )}

        {!loading && !reportData && (
          <Card style={{ marginTop: "24px", textAlign: "center" }}>
            <div style={{ padding: "48px 0" }}>
              <InboxOutlined
                style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }}
              />
              <p style={{ color: "#666" }}>
                Select a date range and summary period, then click &quot;Generate
                Report&quot; to view pool packages data
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PoolReport;
